"use client";

import { useState, type FormEvent } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import type { AuthEntryPoint } from "@/lib/analytics/types";
import { ProviderButtons } from "../social-sign-in";
import { AuthField } from "./auth-field";
import { AuthError, AuthNotice } from "./auth-messages";
import { useSendCode } from "./email-code-step";

function StartFooter(props: { onUsePassword?: () => void; onLogIn?: () => void }) {
  return (
    <div className="space-y-2 text-center text-sm text-muted-foreground">
      <p>
        We&apos;ll email you a 6-digit code to confirm it&apos;s you. By continuing you agree to the{" "}
        <Link href="/terms-conditions" target="_blank" rel="noopener noreferrer" className="underline">Terms</Link> and{" "}
        <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</Link>.
      </p>
      {props.onUsePassword ? (
        <button
          type="button"
          className="min-h-11 font-medium text-foreground underline"
          onClick={props.onUsePassword}
        >
          Log in with a password
        </button>
      ) : null}
      {props.onLogIn ? (
        <p>
          Already have an account?{" "}
          <button type="button" className="min-h-11 font-medium text-foreground underline" onClick={props.onLogIn}>
            Log in
          </button>
        </p>
      ) : null}
    </div>
  );
}

export function StartStep(props: {
  entryPoint: AuthEntryPoint;
  initialEmail?: string;
  notice?: string;
  showProviders: boolean;
  onCodeSent: (email: string) => void;
  onUsePassword?: (email: string) => void;
  onLogIn?: () => void;
}) {
  const [email, setEmail] = useState(props.initialEmail ?? "");
  const sending = useSendCode(props.entryPoint);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const sentTo = await sending.send(email);
    if (sentTo) props.onCodeSent(sentTo);
  };

  return (
    <div className="space-y-5">
      <form className="space-y-3" onSubmit={submit}>
        <AuthNotice message={props.notice} />
        <AuthField label="Email" type="email" autoComplete="email" autoFocus value={email} onChange={setEmail} />
        <AuthError message={sending.error} />
        <Button type="submit" variant="primary" className="w-full" disabled={sending.isSending}>
          {sending.isSending ? "Sending your code…" : "Continue with email"}
        </Button>
      </form>
      {props.showProviders ? <ProviderButtons /> : null}
      <StartFooter onUsePassword={props.onUsePassword ? () => props.onUsePassword?.(email) : undefined} onLogIn={props.onLogIn} />
    </div>
  );
}
